import manifestRaw from './manifest.json' assert { type: 'json' }
import stringsRaw from './strings.json' assert { type: 'json' }
import { assertCuratedManifest, assertCuratedStrings } from './schema'
import { defaultLocale } from '@/i18n/routing'
import type {
  CuratedCollection,
  CuratedCollectionLocaleMap
} from '@/types/collections'

type ManifestCollection = {
  slug: string
  level: number
  coverIcon?: string | null
  item_count: number
}

const manifestData = manifestRaw as unknown
assertCuratedManifest(manifestData)
const parsedManifest = manifestData as {
  collections: Record<string, ManifestCollection>
}

const stringsData = stringsRaw as unknown
assertCuratedStrings(stringsData)
const parsedStrings = stringsData as CuratedCollectionLocaleMap

const FALLBACK_LOCALE = defaultLocale || 'en'

const sortedManifestCollections: ManifestCollection[] = Object.values(
  parsedManifest.collections
).sort((a, b) => {
  if (a.slug !== b.slug) return a.slug.localeCompare(b.slug)
  if (a.level !== b.level) return a.level - b.level
  return 0
})

function resolveLocale(locale?: string): string {
  if (locale && parsedStrings[locale]) return locale
  if (parsedStrings[FALLBACK_LOCALE]) return FALLBACK_LOCALE
  const firstLocale = Object.keys(parsedStrings)[0]
  return firstLocale || FALLBACK_LOCALE
}

function mergeStrings(
  base: ManifestCollection,
  locale?: string
): CuratedCollection {
  const selectedLocale = resolveLocale(locale)
  const localeStrings = parsedStrings[selectedLocale]?.[base.slug]
  const fallbackStrings = parsedStrings[FALLBACK_LOCALE]?.[base.slug]

  const title =
    localeStrings?.title ||
    fallbackStrings?.title ||
    base.slug.replace(/-/g, ' ')
  const description =
    localeStrings?.description ||
    fallbackStrings?.description ||
    'Curated collection'
  const coverLabel = localeStrings?.coverLabel || fallbackStrings?.coverLabel

  return {
    ...base,
    title,
    description,
    coverLabel
  }
}

export function getCuratedCollectionSlugs(): string[] {
  return sortedManifestCollections.map((c) => c.slug)
}

export function getAllCuratedCollections(locale?: string): CuratedCollection[] {
  return sortedManifestCollections.map((base) => mergeStrings(base, locale))
}

export function getCuratedCollection(
  slug: string,
  locale?: string
): CuratedCollection | undefined {
  const base = parsedManifest.collections[slug]
  if (!base) return undefined
  return mergeStrings(base, locale)
}
